import { useState } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { CaptchaField, useCaptcha } from "./Captcha";

const Field = ({ id, label, hint, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm text-[#F0F6FC]">{label}</Label>
    <Input id={id} data-testid={`register-${id}-input`}
      className="h-12 bg-[#0D1117] border-[#30363D] focus-visible:ring-[#3FB950] font-mono" {...props} />
    {hint && <p className="text-xs text-[#6E7681]">{hint}</p>}
  </div>
);

export const useRegisterForm = (onSuccess) => {
  const { t } = useI18n();
  const [form, setForm] = useState({ password: "", confirm: "", nickname: "", email: "", captcha: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { captcha, reload } = useCaptcha();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 4 || form.password.length > 16) return setError(t("errLen"));
    if (!/^[\x21-\x7e]+$/.test(form.password)) return setError(t("errChars"));
    if (form.password !== form.confirm) return setError(t("errMatch"));
    if (!captcha?.captcha_id || form.captcha.trim().length < 5) return setError(t("errCaptchaEmpty"));
    setLoading(true);
    try {
      const { data } = await api.post("/register", {
        password: form.password, nickname: form.nickname, email: form.email,
        captcha_id: captcha.captcha_id, captcha_answer: form.captcha,
      });
      onSuccess({ ...data, password: form.password });
    } catch (err) {
      const code = err.response?.status;
      const d = err.response?.data?.detail;
      if (code === 400) setError(t("errCaptcha"));
      else if (code === 429) setError(t("errRate"));
      else if (code === 422) setError(t("errLen"));
      else if (code === 502) setError(`${t("errOscar")} ${d}`);
      else setError(t("errNet"));
      setForm((f) => ({ ...f, captcha: "" }));
      reload();
    } finally {
      setLoading(false);
    }
  };

  return { form, set, error, loading, captcha, reload, submit };
};

export const RegisterForm = ({ onSuccess }) => {
  const { t } = useI18n();
  const { form, set, error, loading, captcha, reload, submit } = useRegisterForm(onSuccess);

  return (
    <form onSubmit={submit} data-testid="register-form" className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field id="password" label={t("fPassword")} type="password" value={form.password} onChange={set("password")}
          hint={t("pwHint")} autoComplete="new-password" />
        <Field id="confirm" label={t("fConfirm")} type="password" value={form.confirm} onChange={set("confirm")} autoComplete="new-password" />
        <Field id="nickname" label={t("fNick")} value={form.nickname} onChange={set("nickname")} placeholder={t("optional")} />
        <Field id="email" label={t("fEmail")} type="email" value={form.email} onChange={set("email")} placeholder={t("optional")} />
      </div>
      <CaptchaField captcha={captcha} reload={reload} value={form.captcha} onChange={set("captcha")} />
      {error && (
        <div data-testid="register-error" className="flex items-start gap-2 text-sm text-[#F85149] bg-[#F85149]/10 border border-[#F85149]/30 rounded-lg p-3">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}
      <Button type="submit" size="lg" disabled={loading} data-testid="register-submit-btn"
        className="w-full h-12 rounded-full bg-[#3FB950] text-[#0D1117] hover:bg-[#2EA043] font-semibold text-base">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("submitting")}</> : t("submit")}
      </Button>
      <p className="text-xs text-[#6E7681] text-center">{t("pwNote")}</p>
    </form>
  );
};
